import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// Editorial theme colors
const COLORS = {
  cream: '#FAF8F5',
  charcoal: '#2D2A26',
  terracotta: '#C45D3A',
  border: '#2D2A26'
};

const HeroContainer = styled(Box)({
  backgroundColor: COLORS.cream,
  borderBottom: `1px solid ${COLORS.charcoal}`,
  display: 'grid',
  gridTemplateColumns: '1.2fr 1fr',
  minHeight: '500px',
  '@media (max-width: 1024px)': {
    gridTemplateColumns: '1fr',
    minHeight: 'auto'
  }
});

const HeroLeft = styled(Box)({
  padding: '64px 80px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  borderRight: `1px solid ${COLORS.charcoal}`,
  '@media (max-width: 1024px)': {
    padding: '48px 40px',
    borderRight: 'none',
    borderBottom: `1px solid ${COLORS.charcoal}`
  },
  '@media (max-width: 768px)': {
    padding: '40px 24px'
  }
});

const HeroRight = styled(Box)({
  position: 'relative',
  minHeight: '400px',
  '@media (max-width: 1024px)': {
    minHeight: '320px'
  },
  '@media (max-width: 768px)': {
    minHeight: '280px'
  }
});

const HeroImage = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundSize: 'cover',
  backgroundPosition: 'center'
});

const CategoryLabel = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '11px',
  fontWeight: 600,
  color: COLORS.terracotta,
  textTransform: 'uppercase',
  letterSpacing: '3px',
  marginBottom: '16px'
});

const BusinessName = styled(Typography)({
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: 'clamp(32px, 5vw, 52px)',
  fontWeight: 600,
  color: COLORS.charcoal,
  lineHeight: 1.1,
  letterSpacing: '-1px',
  marginBottom: '20px'
});

const Tagline = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '16px',
  fontWeight: 400,
  color: COLORS.charcoal,
  opacity: 0.7,
  lineHeight: 1.6,
  marginBottom: '32px',
  maxWidth: '480px',
  '@media (max-width: 768px)': {
    fontSize: '15px',
    marginBottom: '28px'
  }
});

const Divider = styled(Box)({
  width: '48px',
  height: '2px',
  backgroundColor: COLORS.terracotta,
  marginBottom: '32px'
});

const ContactGrid = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '24px',
  marginBottom: '32px',
  '@media (max-width: 600px)': {
    flexDirection: 'column',
    gap: '16px'
  }
});

const ContactItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  cursor: 'pointer',
  transition: 'color 0.2s ease',
  '&:hover': {
    '& .contact-text': {
      color: COLORS.terracotta
    },
    '& .contact-icon': {
      color: COLORS.terracotta
    }
  }
});

const ContactIcon = styled(Box)({
  '& svg': {
    fontSize: '18px',
    color: COLORS.charcoal,
    opacity: 0.6,
    transition: 'color 0.2s ease'
  }
});

const ContactText = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '14px',
  fontWeight: 500,
  color: COLORS.charcoal,
  transition: 'color 0.2s ease'
});

const CTAButton = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '12px',
  padding: '14px 28px',
  backgroundColor: COLORS.charcoal,
  color: COLORS.cream,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '13px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '1px',
  '&:hover': {
    backgroundColor: COLORS.terracotta,
    '& .arrow-icon': {
      transform: 'translateX(4px)'
    }
  }
});

const ArrowIcon = styled(ArrowForwardIcon)({
  fontSize: '16px',
  transition: 'transform 0.2s ease'
});

const ImagePlaceholder = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: COLORS.charcoal,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  gap: '16px'
});

const PlaceholderLetter = styled(Typography)({
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: '120px',
  fontWeight: 700,
  color: COLORS.cream,
  opacity: 0.08,
  lineHeight: 1,
  '@media (max-width: 768px)': {
    fontSize: '80px'
  }
});

const ImageOverlay = styled(Box)({
  position: 'absolute',
  bottom: '24px',
  left: '24px',
  right: '24px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  '@media (max-width: 768px)': {
    bottom: '16px',
    left: '16px',
    right: '16px'
  }
});

const RatingBadge = styled(Box)({
  backgroundColor: COLORS.cream,
  padding: '10px 16px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px'
});

const RatingText = styled(Typography)({
  fontFamily: "'Space Mono', monospace",
  fontSize: '14px',
  fontWeight: 700,
  color: COLORS.charcoal
});

const RatingLabel = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '11px',
  fontWeight: 500,
  color: COLORS.charcoal,
  opacity: 0.6,
  textTransform: 'uppercase',
  letterSpacing: '1px'
});

const EditorialHeroSection = ({ businessInfo, heroImage }) => {
  const handlePhoneClick = () => {
    if (businessInfo?.phone) {
      window.location.href = `tel:${businessInfo.phone}`;
    }
  };

  const handleEmailClick = () => {
    if (businessInfo?.email) {
      window.location.href = `mailto:${businessInfo.email}`;
    }
  };

  const handleAddressClick = () => {
    if (businessInfo?.address) {
      const encodedAddress = encodeURIComponent(businessInfo.address);
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
    }
  };

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <HeroContainer id="home">
      <HeroLeft>
        {businessInfo?.category && (
          <CategoryLabel>
            {businessInfo.category}
          </CategoryLabel>
        )}

        <BusinessName>
          {businessInfo?.name || 'Your Business'}
        </BusinessName>

        <Divider />

        {businessInfo?.description && (
          <Tagline>
            {businessInfo.description}
          </Tagline>
        )}

        <ContactGrid>
          {businessInfo?.phone && (
            <ContactItem onClick={handlePhoneClick}>
              <ContactIcon className="contact-icon">
                <PhoneIcon />
              </ContactIcon>
              <ContactText className="contact-text">{businessInfo.phone}</ContactText>
            </ContactItem>
          )}

          {businessInfo?.email && (
            <ContactItem onClick={handleEmailClick}>
              <ContactIcon className="contact-icon">
                <EmailIcon />
              </ContactIcon>
              <ContactText className="contact-text">{businessInfo.email}</ContactText>
            </ContactItem>
          )}

          {businessInfo?.address && (
            <ContactItem onClick={handleAddressClick}>
              <ContactIcon className="contact-icon">
                <LocationOnIcon />
              </ContactIcon>
              <ContactText className="contact-text">
                {businessInfo.address.length > 40
                  ? businessInfo.address.substring(0, 40) + '...'
                  : businessInfo.address}
              </ContactText>
            </ContactItem>
          )}
        </ContactGrid>

        <CTAButton onClick={scrollToContact}>
          Get in Touch
          <ArrowIcon className="arrow-icon" />
        </CTAButton>
      </HeroLeft>

      <HeroRight>
        {heroImage ? (
          <>
            <HeroImage sx={{ backgroundImage: `url(${heroImage})` }} />
            {businessInfo?.rating && (
              <ImageOverlay>
                <RatingBadge>
                  <RatingText>{businessInfo.rating}</RatingText>
                  <RatingLabel>Rating</RatingLabel>
                </RatingBadge>
              </ImageOverlay>
            )}
          </>
        ) : (
          <ImagePlaceholder>
            <PlaceholderLetter>
              {(businessInfo?.name || 'B')[0]}
            </PlaceholderLetter>
          </ImagePlaceholder>
        )}
      </HeroRight>
    </HeroContainer>
  );
};

export default EditorialHeroSection;
